class SuccessStorySliderAB {
  constructor(root) {
    this.root = root;
    this.dataEl = root.querySelector('.success-story-data');
    if (!this.dataEl) {
      return;
    }

    try {
      this.slides = JSON.parse(this.dataEl.textContent || '[]');
    } catch (err) {
      console.error('SuccessStorySliderAB: invalid JSON data.', err);
      this.slides = [];
    }

    if (!Array.isArray(this.slides) || this.slides.length === 0) {
      return;
    }

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isTransitioning = false;

    this.imageSection = root.querySelector('[data-ss-image-section]');
    this.sourceDesktopEl = root.querySelector('[data-ss-source-desktop]');
    this.sourceMobileEl = root.querySelector('[data-ss-source-mobile]');
    this.imgEl = root.querySelector('[data-ss-img]');
    this.contentBox = root.querySelector('[data-ss-content-box]');
    this.titleEl = root.querySelector('[data-ss-title]');
    this.p1El = root.querySelector('[data-ss-p1]');
    this.restEl = root.querySelector('[data-ss-rest]');
    this.descEl = root.querySelector('[data-ss-desc]');
    this.readMoreBtn = root.querySelector('[data-ss-read-more]');
    this.closeBtn = root.querySelector('[data-ss-close]');
    this.prevBtn = root.querySelector('[data-ss-prev]');
    this.nextBtn = root.querySelector('[data-ss-next]');
    this.paginationEl = root.querySelector('[data-ss-pagination]');

    this.bindEvents();
    this.observeContentBoxVisibility();
    this.renderSlide(0, true);
    this.updatePagination();
    this.updateNavVisibility();
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goToSlide(this.currentIndex - 1));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goToSlide(this.currentIndex + 1));
    }

    if (this.readMoreBtn) {
      this.readMoreBtn.addEventListener('click', () => this.expand());
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.collapse(true));
    }
  }

  observeContentBoxVisibility() {
    if (!this.contentBox) {
      return;
    }

    const updateVisibility = () => {
      this.visibilityRaf = null;
      const rect = this.contentBox.getBoundingClientRect();
      let inView = rect.bottom > 0 && rect.top < window.innerHeight;

      if (this.closeBtn) {
        const btnRect = this.closeBtn.getBoundingClientRect();
        inView = btnRect.bottom > rect.top && btnRect.top < rect.bottom;
      }

      this.contentBox.classList.toggle('is-in-view', inView);
    };

    const onScroll = () => {
      if (this.visibilityRaf) {
        return;
      }
      this.visibilityRaf = window.requestAnimationFrame(updateVisibility);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
  }

  updateNavVisibility() {
    const shouldHide = this.totalSlides <= 1;
    if (this.prevBtn) this.prevBtn.style.display = shouldHide ? 'none' : '';
    if (this.nextBtn) this.nextBtn.style.display = shouldHide ? 'none' : '';
    if (this.paginationEl) this.paginationEl.style.display = shouldHide ? 'none' : '';
  }

  updatePagination() {
    if (!this.paginationEl) {
      return;
    }

    this.paginationEl.innerHTML = '';
    const isCrowded = this.totalSlides > 8;
    this.paginationEl.classList.toggle('is-crowded', isCrowded);

    if (isCrowded) {
      const count = document.createElement('span');
      count.className = 'success-story-pagination-count';
      count.textContent = `${this.currentIndex + 1} / ${this.totalSlides}`;
      count.setAttribute('aria-hidden', 'true');
      this.paginationEl.appendChild(count);
    }

    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'success-story-pagination-dots';
    this.paginationEl.appendChild(dotsWrap);

    for (let i = 0; i < this.totalSlides; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `success-story-dot${i === this.currentIndex ? ' is-active' : ''}`;
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.goToSlide(i));
      dotsWrap.appendChild(dot);
    }
  }

  goToSlide(index) {
    if (this.isTransitioning || index === this.currentIndex) {
      return;
    }

    if (index < 0) {
      index = this.totalSlides - 1;
    } else if (index >= this.totalSlides) {
      index = 0;
    }

    this.collapse();
    this.renderSlide(index, false);
  }

  renderSlide(index, immediate) {
    const nextSlide = this.slides[index];
    if (!nextSlide) {
      return;
    }

    const doUpdate = () => {
      if (this.titleEl) {
        this.titleEl.textContent = nextSlide.title || '';
      }

      if (this.p1El) {
        this.p1El.innerHTML = nextSlide.p1 || '';
      }

      if (this.restEl) {
        this.restEl.innerHTML = nextSlide.rest || '';
      }

      const fallbackImg = nextSlide.img || '';
      const desktopImg = nextSlide.img_desktop || fallbackImg || nextSlide.img_mobile || '';
      const mobileImg = nextSlide.img_mobile || fallbackImg || nextSlide.img_desktop || '';
      const displayImg = desktopImg || mobileImg || fallbackImg || '';
      const altText = nextSlide.img_alt || nextSlide.title || '';

      if (this.sourceDesktopEl) {
        if (desktopImg) {
          this.sourceDesktopEl.setAttribute('srcset', desktopImg);
        } else {
          this.sourceDesktopEl.removeAttribute('srcset');
        }
      }

      if (this.sourceMobileEl) {
        if (mobileImg) {
          this.sourceMobileEl.setAttribute('srcset', mobileImg);
        } else {
          this.sourceMobileEl.removeAttribute('srcset');
        }
      }

      if (this.imgEl) {
        if (displayImg) {
          this.imgEl.src = displayImg;
          this.imgEl.alt = altText;
          this.imgEl.style.display = '';
          if (this.imageSection) this.imageSection.classList.remove('is-empty');
        } else {
          this.imgEl.removeAttribute('src');
          this.imgEl.alt = '';
          this.imgEl.style.display = 'none';
          if (this.imageSection) this.imageSection.classList.add('is-empty');
        }
      }

      const hasRest = Boolean(nextSlide.has_rest);
      if (this.readMoreBtn) {
        this.readMoreBtn.classList.toggle('is-hidden', !hasRest);
      }

      if (this.descEl) {
        this.descEl.classList.toggle('is-expanded', !hasRest);
      }

      this.currentIndex = index;
      this.updatePagination();
      this.isTransitioning = false;
    };

    if (immediate) {
      doUpdate();
      return;
    }

    this.isTransitioning = true;

    if (this.imgEl) {
      this.imgEl.classList.add('fade-out');
    }

    if (this.contentBox) {
      this.contentBox.classList.add('fade-out');
    }

    window.setTimeout(() => {
      doUpdate();

      if (this.imgEl) {
        this.imgEl.classList.remove('fade-out');
      }

      if (this.contentBox) {
        this.contentBox.classList.remove('fade-out');
      }
    }, 400);
  }

  expand() {
    if (this.descEl) {
      this.descEl.classList.add('is-expanded');
    }

    if (this.readMoreBtn) {
      this.readMoreBtn.classList.add('is-hidden');
    }

    if (this.closeBtn) {
      this.closeBtn.classList.add('is-visible');
    }

    this.root.classList.add('is-reading-mode');

    if (this.descEl) {
      window.setTimeout(() => {
        this.descEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }

  collapse(scrollToCard) {
    if (this.descEl) {
      this.descEl.classList.remove('is-expanded');
    }

    if (this.readMoreBtn) {
      this.readMoreBtn.classList.remove('is-hidden');
    }

    if (this.closeBtn) {
      this.closeBtn.classList.remove('is-visible');
    }

    this.root.classList.remove('is-reading-mode');

    if (scrollToCard) {
      this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.js-success-story-slider');
  sliders.forEach((slider) => {
    new SuccessStorySliderAB(slider);
  });
});
